import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Box,
    Typography,
    alpha,
    Stack,
    Button,
    TextField,
    Checkbox,
    FormControlLabel,
    Link,
    Grid
} from '@mui/material';
import { DownloadOutlined, CheckCircleFilled, FilePdfOutlined } from '@ant-design/icons';
import AnimateButton from 'components/@extended/AnimateButton';
import { PageUrl } from 'const/page-url';
import { LEAD_DOCUMENTS, LEAD_ENDPOINT, LEAD_MAIL_TO } from 'const/documents';

// ==============================|| DOCUMENT REQUEST FORM ||============================== //
//
// ファーストビュー右側に置く資料請求フォーム。
// 送信後は「その場で PDF をダウンロードさせる」ことを最優先にしており、
// リードの通知が失敗してもダウンロードは必ず実行される(通知の成否でユーザーを待たせない)。

type Errors = Partial<Record<'lastName' | 'firstName' | 'tel' | 'email' | 'documents', string>>;

const primaryColor = '#2aa1af';

// 配布資料が1点だけのときは選ばせる意味がないため、チェックボックスではなく
// 「何が届くか」を示すだけの表示に切り替える(複数に戻せば自動で選択式に戻る)。
const isSingleDocument = LEAD_DOCUMENTS.length === 1;

type Props = {
    /** フォーム上部の見出し。設置場所によって言い回しを変えられるようにしている */
    heading?: string;
};

const DocumentRequestForm = ({ heading = 'サービス紹介資料を無料でダウンロード' }: Props) => {
    const [lastName, setLastName] = useState('');
    const [firstName, setFirstName] = useState('');
    const [company, setCompany] = useState('');
    const [tel, setTel] = useState('');
    const [email, setEmail] = useState('');
    // 初期状態で全資料を選択済みにしておき、そのまま送信すればすべて受け取れるようにする
    const [selected, setSelected] = useState<string[]>(LEAD_DOCUMENTS.map((doc) => doc.id));
    const [errors, setErrors] = useState<Errors>({});
    const [submitted, setSubmitted] = useState(false);

    const toggleDocument = (id: string) => {
        setSelected((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
    };

    const validate = (): Errors => {
        const next: Errors = {};
        if (!lastName.trim()) next.lastName = '入力してください';
        if (!firstName.trim()) next.firstName = '入力してください';
        // 数字・ハイフン・括弧・空白のみを許容し、数字が10桁以上あることだけを確認する
        if (!tel.trim()) {
            next.tel = '電話番号を入力してください';
        } else if ((tel.match(/\d/g) || []).length < 10) {
            next.tel = '電話番号の桁数をご確認ください';
        }
        if (!email.trim()) {
            next.email = 'メールアドレスを入力してください';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
            next.email = 'メールアドレスの形式をご確認ください';
        }
        if (selected.length === 0) next.documents = 'ご希望の資料を1つ以上お選びください';
        return next;
    };

    /** 選択された PDF を順番にダウンロードさせる(同時に複数開くとブラウザにブロックされるため間隔を空ける) */
    const downloadDocuments = () => {
        const targets = LEAD_DOCUMENTS.filter((doc) => selected.includes(doc.id));
        targets.forEach((doc, index) => {
            window.setTimeout(() => {
                const anchor = document.createElement('a');
                anchor.href = doc.file;
                anchor.download = doc.downloadName;
                document.body.appendChild(anchor);
                anchor.click();
                document.body.removeChild(anchor);
            }, index * 700);
        });
    };

    /** リード情報の通知。エンドポイント未設定の場合はメールソフトの下書きを開く */
    const notifyLead = () => {
        const documentTitles = LEAD_DOCUMENTS.filter((doc) => selected.includes(doc.id))
            .map((doc) => doc.title)
            .join('、');
        const requestedAt = new Date().toLocaleString('ja-JP');

        if (LEAD_ENDPOINT) {
            // 送信先は自社AWS内のLambda(aws/terraform/functions/lead-notify/)。
            // メールの件名・本文の組み立てはLambda側で行うため、ここでは値だけを渡す。
            // 通知の失敗でダウンロードを止めないよう、結果は待たずに握りつぶす。
            fetch(LEAD_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    lastName: lastName.trim(),
                    firstName: firstName.trim(),
                    company: company.trim(),
                    tel: tel.trim(),
                    email: email.trim(),
                    documents: documentTitles,
                    requestedAt
                })
            }).catch(() => undefined);
            return;
        }

        // フォールバック: 送信先が未設定の場合のみ、訪問者のメールソフトで下書きを開く
        const body = [
            `氏名: ${lastName.trim()} ${firstName.trim()}`,
            `事業所名: ${company.trim() || '(未入力)'}`,
            `電話番号: ${tel.trim()}`,
            `メールアドレス: ${email.trim()}`,
            `希望資料: ${documentTitles}`,
            `請求日時: ${requestedAt}`
        ].join('\n');
        window.open(
            `mailto:${LEAD_MAIL_TO}?subject=${encodeURIComponent('OHANA AI 資料請求')}&body=${encodeURIComponent(body)}`,
            '_blank'
        );
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        const nextErrors = validate();
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) return;

        downloadDocuments();
        notifyLead();
        setSubmitted(true);
    };

    const fieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            bgcolor: '#fff',
            fontSize: '0.92rem'
        }
    };

    const RequiredMark = ({ optional = false }: { optional?: boolean }) => (
        <Box
            component="span"
            sx={{
                fontSize: '0.68rem',
                fontWeight: 800,
                px: 0.9,
                py: 0.3,
                mr: 1,
                borderRadius: '4px',
                color: optional ? '#8a9393' : '#e2574c',
                border: `1px solid ${optional ? '#c8d0d0' : alpha('#e2574c', 0.5)}`
            }}
        >
            {optional ? '任意' : '必須'}
        </Box>
    );

    const Label = ({ children, optional = false }: { children: React.ReactNode; optional?: boolean }) => (
        <Stack direction="row" alignItems="center" sx={{ mb: 0.8 }}>
            <RequiredMark optional={optional} />
            <Typography component="span" sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#374151' }}>
                {children}
            </Typography>
        </Stack>
    );

    // 送信後の完了表示
    if (submitted) {
        return (
            <Box
                sx={{
                    p: { xs: 3.5, md: 4.5 },
                    borderRadius: '28px',
                    bgcolor: '#fff',
                    border: `1px solid ${alpha(primaryColor, 0.16)}`,
                    boxShadow: `0 22px 50px ${alpha(primaryColor, 0.12)}`,
                    textAlign: 'center'
                }}
            >
                <Box aria-hidden="true" sx={{ color: primaryColor, fontSize: '3rem', lineHeight: 1, mb: 2 }}>
                    <CheckCircleFilled />
                </Box>
                <Typography variant="h3" component="p" sx={{ fontWeight: 900, fontSize: '1.3rem', color: '#1a1a1a', mb: 2 }}>
                    ダウンロードを開始しました
                </Typography>
                <Typography component="p" sx={{ color: '#666', fontSize: '0.92rem', lineHeight: 1.9, mb: 3 }}>
                    ダウンロードが始まらない場合は、下のリンクから直接お受け取りください。<br />
                    内容について詳しくお聞きになりたい場合は、担当者よりご説明いたします。
                </Typography>

                <Stack spacing={1.2} sx={{ mb: 3 }}>
                    {LEAD_DOCUMENTS.filter((doc) => selected.includes(doc.id)).map((doc) => (
                        <Link
                            key={doc.id}
                            href={doc.file}
                            download={doc.downloadName}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.2,
                                p: 1.6,
                                borderRadius: '12px',
                                border: `1px solid ${alpha(primaryColor, 0.25)}`,
                                color: primaryColor,
                                fontWeight: 700,
                                fontSize: '0.88rem',
                                textDecoration: 'none',
                                textAlign: 'left',
                                '&:hover': { bgcolor: alpha(primaryColor, 0.06) }
                            }}
                        >
                            <FilePdfOutlined aria-hidden="true" />
                            {doc.title}
                        </Link>
                    ))}
                </Stack>

                <Button
                    onClick={() => setSubmitted(false)}
                    sx={{ color: '#8a9393', fontSize: '0.85rem', fontWeight: 700 }}
                >
                    {isSingleDocument ? '資料をもう一度受け取る' : '他の資料も受け取る'}
                </Button>
            </Box>
        );
    }

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            aria-label="資料請求フォーム"
            sx={{
                p: { xs: 3, md: 4 },
                borderRadius: '28px',
                bgcolor: '#fff',
                border: `1px solid ${alpha(primaryColor, 0.16)}`,
                boxShadow: `0 22px 50px ${alpha(primaryColor, 0.12)}`
            }}
        >
            <Typography
                variant="h3"
                component="h2"
                sx={{ fontWeight: 900, fontSize: { xs: '1.15rem', md: '1.3rem' }, color: '#1a1a1a', textAlign: 'center', mb: 0.8 }}
            >
                {heading}
            </Typography>
            <Typography component="p" sx={{ color: '#8a9393', fontSize: '0.8rem', textAlign: 'center', mb: 3 }}>
                ご入力後、その場でPDFをお受け取りいただけます
            </Typography>

            {/* 希望資料。1点のみの配布なので、選択式ではなく内容の提示にしている */}
            {isSingleDocument ? (
                <Box sx={{ mb: 2.5 }}>
                    <Stack direction="row" alignItems="center" sx={{ mb: 0.8 }}>
                        <Typography component="span" sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#374151' }}>
                            お受け取りいただける資料
                        </Typography>
                    </Stack>
                    <Stack
                        direction="row"
                        spacing={1.2}
                        alignItems="flex-start"
                        sx={{
                            borderRadius: '12px',
                            border: `1px solid ${primaryColor}`,
                            bgcolor: alpha(primaryColor, 0.04),
                            px: 1.8,
                            py: 1.4
                        }}
                    >
                        <Box aria-hidden="true" sx={{ color: primaryColor, fontSize: '1.1rem', mt: '1px' }}>
                            <FilePdfOutlined />
                        </Box>
                        <Box>
                            <Typography
                                component="span"
                                sx={{ display: 'block', fontWeight: 800, fontSize: '0.88rem', color: '#1a1a1a', lineHeight: 1.5 }}
                            >
                                {LEAD_DOCUMENTS[0].title}
                                <Box component="span" sx={{ color: '#9aa5a5', fontWeight: 600, fontSize: '0.72rem', ml: 0.8 }}>
                                    {LEAD_DOCUMENTS[0].volume}
                                </Box>
                            </Typography>
                            <Typography
                                component="span"
                                sx={{ display: 'block', color: '#8a9393', fontSize: '0.74rem', lineHeight: 1.6, mt: 0.3 }}
                            >
                                {LEAD_DOCUMENTS[0].description}
                            </Typography>
                        </Box>
                    </Stack>
                </Box>
            ) : (
            <Box sx={{ mb: 2.5 }}>
                <Label>ご希望の資料（複数選択可）</Label>
                <Stack spacing={1}>
                    {LEAD_DOCUMENTS.map((doc) => {
                        const checked = selected.includes(doc.id);
                        return (
                            <Box
                                key={doc.id}
                                sx={{
                                    borderRadius: '12px',
                                    border: `1px solid ${checked ? primaryColor : '#e0e6e6'}`,
                                    bgcolor: checked ? alpha(primaryColor, 0.04) : '#fff',
                                    px: 1.5,
                                    py: 1,
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                <FormControlLabel
                                    sx={{ m: 0, alignItems: 'flex-start', width: '100%' }}
                                    control={
                                        <Checkbox
                                            checked={checked}
                                            onChange={() => toggleDocument(doc.id)}
                                            sx={{ p: 0.5, mr: 1, mt: '-2px', color: '#c8d0d0', '&.Mui-checked': { color: primaryColor } }}
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography
                                                component="span"
                                                sx={{ display: 'block', fontWeight: 800, fontSize: '0.86rem', color: '#1a1a1a', lineHeight: 1.5 }}
                                            >
                                                {doc.title}
                                                <Box component="span" sx={{ color: '#9aa5a5', fontWeight: 600, fontSize: '0.72rem', ml: 0.8 }}>
                                                    {doc.volume}
                                                </Box>
                                            </Typography>
                                            <Typography
                                                component="span"
                                                sx={{ display: 'block', color: '#8a9393', fontSize: '0.74rem', lineHeight: 1.6, mt: 0.3 }}
                                            >
                                                {doc.description}
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Box>
                        );
                    })}
                </Stack>
                {errors.documents && (
                    <Typography component="p" sx={{ color: '#e2574c', fontSize: '0.75rem', mt: 0.8, fontWeight: 700 }}>
                        {errors.documents}
                    </Typography>
                )}
            </Box>
            )}

            {/* 氏名 */}
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                    <Label>姓</Label>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="田中"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        error={Boolean(errors.lastName)}
                        helperText={errors.lastName}
                        sx={fieldSx}
                    />
                </Grid>
                <Grid item xs={6}>
                    <Label>名</Label>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="太郎"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        error={Boolean(errors.firstName)}
                        helperText={errors.firstName}
                        sx={fieldSx}
                    />
                </Grid>
            </Grid>

            <Box sx={{ mb: 2 }}>
                <Label>電話番号</Label>
                <TextField
                    fullWidth
                    size="small"
                    type="tel"
                    placeholder="0123456789"
                    value={tel}
                    onChange={(e) => setTel(e.target.value)}
                    error={Boolean(errors.tel)}
                    helperText={errors.tel}
                    sx={fieldSx}
                />
            </Box>

            <Box sx={{ mb: 2 }}>
                <Label>メールアドレス</Label>
                <TextField
                    fullWidth
                    size="small"
                    type="email"
                    placeholder="xxxx@xxxx.jp"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={Boolean(errors.email)}
                    helperText={errors.email}
                    sx={fieldSx}
                />
            </Box>

            <Box sx={{ mb: 3 }}>
                <Label optional>事業所名</Label>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="〇〇放課後等デイサービス"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    sx={fieldSx}
                />
            </Box>

            <AnimateButton>
                <Button
                    type="submit"
                    fullWidth
                    size="large"
                    variant="contained"
                    endIcon={<DownloadOutlined aria-hidden="true" />}
                    sx={{
                        bgcolor: primaryColor,
                        py: 1.8,
                        borderRadius: '14px',
                        fontSize: '1.02rem',
                        fontWeight: 800,
                        boxShadow: `0 10px 24px ${alpha(primaryColor, 0.3)}`
                    }}
                >
                    下記に同意して資料をダウンロード
                </Button>
            </AnimateButton>

            <Typography component="p" sx={{ color: '#8a9393', fontSize: '0.75rem', textAlign: 'center', mt: 1.8, lineHeight: 1.7 }}>
                <Link component={RouterLink} to={PageUrl.PrivacyPolicy.urlWithSlash} sx={{ color: primaryColor, fontWeight: 700 }}>
                    個人情報の取扱い
                </Link>
                に同意してお申し込みください
            </Typography>
        </Box>
    );
};

export default DocumentRequestForm;
